// Stream Chat service (isomorphic; no react-native dependency)

import type { StreamUser } from '@cobu/types'

let StreamChat: any = null
let streamChatInstance: any = null
let streamApiKey: string | null = null

export function setStreamApiKey(apiKey: string) {
  streamApiKey = apiKey
}

function resolveApiKey(): string | null {
  if (streamApiKey) {
    return streamApiKey
  }
  // Allow env-based configuration when bundler inlines env
  const envKey = import.meta.env.VITE_STREAM_TOKEN
  return envKey
}

/**
 * Initializes the StreamChat client if not already initialized.
 * Returns null on web platform or if Stream Chat is not available.
 */
export async function getStreamChatClient(
  user: StreamUser,
  token: string,
  apiKeyOverride?: string,
): Promise<any | null> {
  try {
    if (!StreamChat) {
      const mod = await import('stream-chat')
      StreamChat = (mod as any).StreamChat
    }
    if (!streamChatInstance) {
      const apiKey = apiKeyOverride ?? resolveApiKey()
      if (!apiKey) {
        throw new Error('STREAM_API_KEY missing. Call setStreamApiKey() or set env STREAM_API_KEY')
      }
      streamChatInstance = StreamChat.getInstance(apiKey)
    }

    if (!streamChatInstance.userID) {
      await streamChatInstance.connectUser(
        {
          id: user.uuid,
          name: user.displayName,
          image: user.profileImage,
        },
        token,
      )
    }

    return streamChatInstance
  } catch (error) {
    console.error('[stream] Error initializing StreamChat:', error)
    throw error
  }
}

export async function disconnectStreamChat(): Promise<void> {
  if (!streamChatInstance) {
    return
  }

  try {
    await streamChatInstance.disconnectUser()
    streamChatInstance = null
  } catch (error) {
    console.error('[stream] Error disconnecting from StreamChat:', error)
  }
}

export function isStreamChatAvailable(): boolean {
  // On web, we lazy-load the SDK; consider it available
  return true
}

export function getCurrentStreamChatClient(): any | null {
  return streamChatInstance
}

export async function createDirectMessageChannel(
  currentUserId: string,
  targetUserId: string,
): Promise<any | null> {
  if (!streamChatInstance) {
    return null
  }

  try {
    const channel = streamChatInstance.channel('messaging', {
      members: [currentUserId, targetUserId],
    })
    await channel.watch()
    return channel
  } catch (error) {
    console.error('[stream] Error creating DM channel:', error)
    throw error
  }
}

export type { StreamUser }


